import { licenseStatusSchema } from '../../../../backend/extraTypes';
import { getManualCredentialVerificationExpirationState, } from '../../../../backend/features/credentialing-verification/shared';
import { requestLicenseCheckFunctionDef } from '../../../../backend/features/credentialing-verification/shared/platform-function-types';
import { useCredentialingTranslation } from '../../../../ui/hooks';
import { TextCell } from '@juvare/common-data-table-ui/DataTable';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import WarningIcon from '@mui/icons-material/Warning';
import { Button } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatusCheckCell } from './StatusCheckCell';
export function useLicenseCheckLatestStatusColumns(reviewProfileTabName) {
    const { t } = useCredentialingTranslation();
    const userState = useUserState();
    const navigationContext = React.useContext(NavigationContext);
    const navigate = useNavigate();
    const requestLicenseCheck = useTypedPlatformFunction(requestLicenseCheckFunctionDef);
    const handleVerify = React.useCallback((personId) => (e) => {
        e.stopPropagation();
        showConfirmDialog({
            title: t('table.credentialingVerification.verify.title'),
            onConfirm: () => {
                requestLicenseCheck({ personId, requestedBy: userState.displayName })
                    .then(() => {
                    showInfoSnackbar({ message: t('table.credentialingVerification.verify.licenseCheckRequested') });
                })
                    .catch(handleErrorDialog);
            },
        });
    }, [requestLicenseCheck, t, userState.displayName]);
    const handleReviewProfile = React.useCallback((personId) => (e) => {
        e.stopPropagation();
        navigationContext.navigate(() => {
            navigate(`/accounts/${personId}?tab=${reviewProfileTabName}`);
        });
    }, [navigate, navigationContext, reviewProfileTabName]);
    const { credentialVerificationExpirationPeriod } = useSelector(getSettings);
    return React.useMemo(() => [
        {
            headerLabel: t('table.credentialingVerification.personName'),
            sortKey: 'personName',
            cellFunction: ({ columns }) => React.createElement(TextCell, null, columns.personName),
        },
        {
            headerLabel: t('table.credentialingVerification.occupationName'),
            sortKey: 'occupationName',
            cellFunction: ({ columns }) => React.createElement(TextCell, null, columns.occupationName),
        },
        {
            headerLabel: t('table.credentialingVerification.credentialName'),
            sortKey: 'credentialName',
            cellFunction: ({ columns }) => React.createElement(TextCell, null, columns.credentialName),
        },
        {
            headerLabel: t('table.credentialingVerification.status'),
            sortKey: 'status',
            cellFunction: ({ columns }) => {
                const result = licenseStatusSchema.safeParse(columns.status);
                const status = result.success ? result.data : 'unknown';
                return React.createElement(StatusCheckCell, { status: status });
            },
        },
        {
            cellFunction: ({ columns }) => {
                const { isExpiring } = getManualCredentialVerificationExpirationState(credentialVerificationExpirationPeriod, columns);
                return (React.createElement(TextCell, null, isExpiring && React.createElement(WarningIcon, { color: 'warning', titleAccess: t('table.credentialingVerification.expiresSoon') })));
            },
            tableCellSx: {
                display: {
                    width: '24px',
                    paddingLeft: 0,
                    paddingRight: 0,
                    textAlign: 'right',
                    xs: 'none',
                    sm: 'table-cell',
                },
            },
        },
        {
            headerLabel: t('table.credentialingVerification.requestDate'),
            sortKey: 'requestDate',
            cellFunction: ({ columns }) => {
                if (!columns.requestDate || (!columns.jasBoard && columns.status !== 'verified')) {
                    return '';
                }
                return React.createElement(TextCell, null, formatDateTime(new Date(columns.requestDate)));
            },
        },
        {
            headerLabel: t('table.credentialingVerification.requestedBy'),
            sortKey: 'requestedBy',
            cellFunction: ({ columns }) => {
                if (!columns.jasBoard && columns.status !== 'verified') {
                    return '';
                }
                return React.createElement(TextCell, null, columns.requestedBy);
            },
        },
        {
            cellFunction: ({ columns }) => {
                if (columns.jasBoard) {
                    const result = licenseStatusSchema.safeParse(columns.status);
                    const status = result.success ? result.data : 'unknown';
                    switch (status) {
                        case 'multipleMatchesFound':
                        case 'inactive':
                        case 'outOfState':
                        case 'expired':
                        case 'cvoExpired':
                        case 'readyToVerify':
                        case 'notChecked':
                        case 'notVerified':
                            return (React.createElement(Button, { onClick: handleVerify(columns.personId), onKeyDown: (event) => event.key === 'Enter' || event.key === 'Space'
                                    ? handleVerify(columns.personId)(event)
                                    : undefined, startIcon: React.createElement(ContentPasteSearchIcon, null) }, t('table.credentialingVerification.verify.label')));
                        default:
                            return null;
                    }
                }
                return (React.createElement(Button, { onClick: handleReviewProfile(columns.personId), onKeyDown: (event) => event.key === 'Enter' || event.key === 'Space' ? handleReviewProfile(columns.personId)(event) : undefined, startIcon: React.createElement(PersonSearchIcon, null) }, t('table.credentialingVerification.actions.reviewProfile')));
            },
        },
    ], [t, credentialVerificationExpirationPeriod, handleReviewProfile, handleVerify]);
}
